<template>
    <BaseTooltip :disabled="showLabel" side="right">
        <template #trigger>
            <component
                :is="tag"
                v-bind="linkAttrs"
                :data-testid="testId"
                :aria-label="ariaLabel ?? title"
                :aria-current="current ? 'page' : undefined"
                class="menu-item nav-item flex items-center gap-3 min-w-0"
                :class="{ 'justify-center nav-item-rail w-auto self-center': !showLabel, 'w-full': showLabel }"
                @click="emit('click')">
                <span class="relative shrink-0 leading-none">
                    <component :is="icon" v-if="typeof icon !== 'string'" class="nav-icon" aria-hidden="true" />
                    <i v-else class="bi nav-icon leading-none" :class="icon" aria-hidden="true"></i>
                    <BaseCountBadge v-if="!showLabel && count > 0" :count="count" class="absolute -top-1.5 -right-1.5" aria-hidden="true" />
                    <NotificationMarble v-else-if="!showLabel && notified" :size="1" class="absolute -top-0.5 -right-0.5" :class="currentRowIndicatorClass" aria-hidden="true" />
                </span>
                <template v-if="showLabel">
                    <span class="flex-1 min-w-0 truncate text-left">{{ title }}</span>
                    <BaseCountBadge v-if="count > 0" :count="count" :ringed="false" :class="currentRowIndicatorClass" aria-hidden="true" />
                    <!-- Same width as the count pill so the dot's centre lines up with the pill's. -->
                    <span v-else-if="notified" class="flex w-4.5 shrink-0 justify-center">
                        <NotificationMarble :size="2" :class="currentRowIndicatorClass" aria-hidden="true" />
                    </span>
                    <slot name="trailing" />
                </template>
            </component>
        </template>
        <template v-if="!showLabel" #content>{{ title }}</template>
    </BaseTooltip>
</template>

<script setup lang="ts">
import BaseCountBadge from "~vue/components/BaseCountBadge.vue";
import BaseTooltip from "~vue/components/BaseTooltip.vue";
import NotificationMarble from "~vue/components/NotificationMarble.vue";
import BaseLink from "~vue/router/BaseLink.vue";
import { type Component, computed } from "vue";

type MainMenuRowProps = {
    title: string;
    icon: string | Component;
    href?: string;
    // Plain anchor instead of an Inertia navigation (external sites, reverse-proxied pages).
    plainAnchor?: boolean;
    external?: boolean;
    method?: string;
    data?: Record<string, unknown>;
    testId?: string;
    ariaLabel?: string;
    current?: boolean;
    showLabel?: boolean;
    count?: number;
    notified?: boolean;
};

const { href, current = false, plainAnchor = false, external = false, method, data, showLabel = true, count = 0, notified = false } = defineProps<MainMenuRowProps>();

const emit = defineEmits<{ click: [] }>();

const tag = computed(() => {
    if (!href) {
        return "button";
    }
    return plainAnchor || external ? "a" : BaseLink;
});

// The selected row's background is the same magenta as the indicators, so invert them there.
const currentRowIndicatorClass = computed(() => (current ? "bg-nav-sidebar-item-active-text text-nav-sidebar-item-active-bg" : undefined));

const linkAttrs = computed(() => {
    if (tag.value === "button") {
        return { type: "button" };
    }
    if (tag.value === "a") {
        return { href, target: external ? "_blank" : undefined, rel: external ? "noopener noreferrer" : undefined };
    }
    return { href, method, data, preserveScroll: false };
});
</script>
